import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [open, setOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'sales', phone: '', is_active: true
  });
  const [roleForm, setRoleForm] = useState({ name: '', description: '', privileges: [] });

  useEffect(() => {
    fetchStaff();
    fetchRoles();
    fetchPrivileges();
  }, []);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`, authHeaders());
      setStaff(response.data);
    } catch (error) {
      toast.error('Failed to fetch staff');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`, authHeaders());
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles');
    }
  };

  const fetchPrivileges = async () => {
    try {
      const response = await axios.get(`${API}/privileges`, authHeaders());
      setPrivileges(response.data);
    } catch (error) {
      console.error('Failed to fetch privileges');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        const { password, ...updateData } = formData;
        await axios.put(`${API}/staff/${editingStaff.id}`, updateData, authHeaders());
        toast.success('Staff updated successfully');
      } else {
        await axios.post(`${API}/auth/register`, formData, authHeaders());
        toast.success('Staff added successfully');
      }
      setOpen(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: 'sales', phone: '', is_active: true });
    setEditingStaff(null);
  };

  const openEditDialog = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      password: '',
      role: staffMember.role,
      phone: staffMember.phone || '',
      is_active: staffMember.is_active
    });
    setOpen(true);
  };

  /* ---------------- Roles & Privileges ---------------- */

  const resetRoleForm = () => {
    setRoleForm({ name: '', description: '', privileges: [] });
    setEditingRole(null);
  };

  const openRoleEdit = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description || '', privileges: role.privileges || [] });
    setRoleOpen(true);
  };

  const togglePrivilege = (key) => {
    setRoleForm((f) => ({
      ...f,
      privileges: f.privileges.includes(key)
        ? f.privileges.filter((p) => p !== key)
        : [...f.privileges, key]
    }));
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(`${API}/roles/${editingRole.id}`, roleForm, authHeaders());
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, roleForm, authHeaders());
        toast.success('Role created');
      }
      setRoleOpen(false);
      resetRoleForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await axios.delete(`${API}/roles/${role.id}`, authHeaders());
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      sales: 'bg-blue-100 text-blue-800',
      'inventory manager': 'bg-green-100 text-green-800',
      financials: 'bg-orange-100 text-orange-800'
    };
    return colors[role.toLowerCase()] || 'bg-slate-100 text-slate-800';
  };

  const roleNames = roles.length
    ? roles.map((r) => r.name)
    : ['admin', 'sales', 'inventory manager', 'financials'];

  return (
    <div data-testid="staff-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
        <p className="text-slate-600 mt-1">Manage staff members, roles and privileges</p>
      </div>

      <Tabs defaultValue="staff">
        <TabsList>
          <TabsTrigger value="staff">Staff</TabsTrigger>
          <TabsTrigger value="roles" data-testid="roles-tab">Roles &amp; Privileges</TabsTrigger>
        </TabsList>

        <TabsContent value="staff">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-staff-button" onClick={resetForm}>
                  <Plus className="mr-2" size={16} />
                  Add Staff
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="staff-dialog" className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" data-testid="staff-name-input" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" data-testid="staff-email-input" type="email" value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} required disabled={!!editingStaff} />
                  </div>
                  {!editingStaff && (
                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <Input id="password" data-testid="staff-password-input" type="password" value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingStaff} minLength={8} />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (for login codes)</Label>
                    <Input id="phone" data-testid="staff-phone-input" value={formData.phone} required
                      placeholder="0712345678"
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                      <SelectTrigger data-testid="staff-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roleNames.map((name) => (
                          <SelectItem key={name} value={name} className="capitalize">{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch id="is_active" data-testid="staff-active-switch" checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                    <Label htmlFor="is_active">Active</Label>
                  </div>
                  <Button data-testid="save-staff-button" type="submit" className="w-full">
                    {editingStaff ? 'Update Staff' : 'Add Staff'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500">
                      No staff members found.
                    </TableCell>
                  </TableRow>
                ) : (
                  staff.map((member) => (
                    <TableRow key={member.id} data-testid={`staff-row-${member.id}`}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.phone || '-'}</TableCell>
                      <TableCell>
                        <Badge className={getRoleColor(member.role)} variant="outline">
                          {member.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={member.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'} variant="outline">
                          {member.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>{new Date(member.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button data-testid={`edit-staff-${member.id}`} variant="ghost" size="icon"
                          onClick={() => openEditDialog(member)}>
                          <Pencil size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="roles">
          <div className="flex justify-end mb-4">
            <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-role-button" onClick={resetRoleForm}>
                  <Plus className="mr-2" size={16} />
                  Create Role
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="role-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingRole ? `Edit Role: ${editingRole.name}` : 'Create Role'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleRoleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="role-name">Role Name *</Label>
                    <Input id="role-name" data-testid="role-name-input" value={roleForm.name}
                      onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                      required disabled={!!editingRole} placeholder="e.g. quality officer" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role-description">Description</Label>
                    <Textarea id="role-description" rows={2} value={roleForm.description}
                      onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                      placeholder="What this role is for" />
                  </div>
                  <div className="space-y-2">
                    <Label>Privileges *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 border rounded-md p-3">
                      {privileges.map((p) => (
                        <label key={p.key} className="flex items-start gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={roleForm.privileges.includes(p.key)}
                            onCheckedChange={() => togglePrivilege(p.key)}
                            data-testid={`priv-${p.key}`}
                          />
                          <span>{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button data-testid="save-role-button" type="submit" className="w-full">
                    {editingRole ? 'Update Role' : 'Create Role'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Privileges</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id} data-testid={`role-row-${role.name}`}>
                    <TableCell className="font-medium capitalize">
                      <span className="inline-flex items-center gap-1">
                        {role.name === 'admin' && <ShieldCheck size={14} className="text-purple-600" />}
                        {role.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{role.description}</TableCell>
                    <TableCell className="max-w-md">
                      {role.name === 'admin' ? (
                        <Badge className="bg-purple-100 text-purple-800">all privileges</Badge>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(role.privileges || []).map((p) => (
                            <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={role.is_system ? 'bg-slate-100' : 'bg-blue-50 text-blue-700'}>
                        {role.is_system ? 'built-in' : 'custom'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {role.name !== 'admin' && (
                        <Button variant="ghost" size="icon" onClick={() => openRoleEdit(role)} data-testid={`edit-role-${role.name}`}>
                          <Pencil size={16} />
                        </Button>
                      )}
                      {!role.is_system && (
                        <Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteRole(role)}>
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
